export type VerificationTask = {
  olletUrlString: string,
  verificationStarted: () => Promise<void>
  verificationResult: () => Promise<Response>
}

export async function startIdentityVerification(
  verifierBaseURL: string, // RequestInfo | URL,
  init?: RequestInit,
  timeout: number = 300  /// timeout is seconds
): Promise<VerificationTask> {
  const response = await fetch(verifierBaseURL + '/buildOidc4vpRequest', init)
  // TODO: validate response
  const headers = Array.from(response.headers.entries()).map(([key, value]) => `${key}: ${value}`).join('\n')
  console.log("> Verifier headers:\n========\n" + headers + "\n========")
  // const newHeaders = new Headers(headers.split('\n').map((h) => {
  //   const i = h.indexOf(': ')
  //   return [h.substring(0, i), h.substring(i+1)]
  // }))

  const worker_uri = response.headers.get('ollet-worker-url') as string

  const ollet_url = Promise.withResolvers<string>()

  const onStart = Promise.withResolvers<void | string>()
  onStart.promise.catch(err => err) // Absorbs the error temporarily

  const onResult = Promise.withResolvers<RequestInit | Error>()
  onResult.promise.catch(err => err) // Absorbs the error temporarily

  const verificationStarted = async () => {
    const error = await onStart.promise
    if (typeof error !== "undefined") {
      throw error
    }
  }

  const reject = (reason: string = 'Identity verification failed') => {
    console.log('Verification failed: ' + reason)
    ollet_url.reject(reason)
    onStart.reject(reason)
    open.reject(reason)
    onResult.resolve(new Error(reason))
  }

  const wsUrl = new URL(worker_uri).href.replace(/^http/, 'ws')
  const websocket = new WebSocket(wsUrl)

  let timerId: ReturnType<typeof setTimeout> | null = null
  const cancelTimeout = () => {
    if (timerId) {
      clearTimeout(timerId)
    }
    timerId = null
  }
  const startTimeout = (delay = timeout) => {
    cancelTimeout()
    timerId = setTimeout(() => {
      websocket.close()
      reject("Operation timed out")
    }, delay * 1000)
  }
  
  startTimeout()

  let success = false

  const open = Promise.withResolvers<void | string>()
  open.promise.catch(err => err) // Absorbs the error temporarily
 
  if (websocket.readyState === WebSocket.OPEN) {
    open.resolve()
  } else {
    websocket.onopen = () => {
      open.resolve()
    }
  }

  websocket.onclose = (event: CloseEvent) => {
    // websocket.onopen = null
    // websocket.onmessage = null
    // websocket.onclose = null
    // websocket.onerror = null
    console.log("Connection closed:", event.reason)
    if (!success) {
      reject(`Closed by server, code: ${event.code}, reason: ${event.reason}, clean: ${event.wasClean}`)
    }
  }

  websocket.onerror  = (event: Event) => {
    websocket.close()
    console.error("WebSocket error:", event)
    // should we implement retry logic here?
    reject('Connection error') // commented out to get better info from onclose()
  }

  websocket.onmessage = async (event: MessageEvent) => {
    // console.log(`Received message: ${event.data}`)
    try {
      if (typeof event.data === 'string') {
        ollet_url.resolve(event.data)
      } else {
        const data = event.data as Blob
        if (data.size == 0) {
          console.log('Verification started')
          startTimeout()
          if (onStart) {
            onStart.resolve()
          }
        } else {
          cancelTimeout()
          const result = await unpack(event.data)
          success = true
          websocket.close()
          onResult.resolve(result)
        }
      }
    } catch(e) {
      console.error(e)
      websocket.close()
      reject()
    }
  }

  const verificationResult = async () => {
    try {
      const result = await onResult.promise
      if (result instanceof Error) {
        throw result.message
      }
      return fetch(verifierBaseURL + '/processOidc4vpResponse', result)
    } catch (error: unknown) {
      const reason = (typeof error === "string") ? error :
        (error instanceof Error) ? error.message : 'unknown error'
      return fetch(verifierBaseURL + '/processOidc4vpResponse', {
        method: 'POST',
        headers: [['ollet-error', reason]]
      })
    }
  }

  await open.promise
  console.log("Connection opened!")

  websocket.send(await pack(response))

  return {
    olletUrlString: await ollet_url.promise,
    verificationStarted,
    verificationResult
  }
}

async function unpack(encoded: Blob): Promise<RequestInit> {
  const text = await encoded.text()
  const i = text.indexOf('\r\n\r\n')
  const hdrStr = text.substring(0, i)
  const body = encoded.slice(new Blob([hdrStr]).size + 4)
  const headers = new Headers(hdrStr.split('\r\n').map((h) => {
    const i = h.indexOf(': ')
    return [h.substring(0, i), h.substring(i+1)]
  }))
  console.log("> Completion headers:\n========\n" + hdrStr + "\n========")
  return { method: 'POST', headers, body }
}

async function pack(r: Request | Response): Promise<Blob> {
  const headers = Array.from(r.headers.entries()).map(([key, value]) => `${key}: ${value}`).join('\r\n') + '\r\n\r\n'
  const encoded = new Blob([Uint8Array.from(headers, (c: string) => c.charCodeAt(0)).buffer, await r.blob()])
  return encoded
}
